import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import { AnimatePresence, motion } from "framer-motion";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AutorenewRounded from "@mui/icons-material/AutorenewRounded";
import { isDarkMode } from "../../utils/constants";
import LoadingPhaseReadableText from "./LoadingPhaseReadableText";
import { isSportsLoadingPhase } from "./loadingPhaseCatalog";
import { getLoadingPhaseMeta } from "./loadingPhaseVisuals";
import { loadingPhaseDimensionsFor } from "./loadingPhaseDimensions";
import { buildLoadingPhaseViewModel } from "./loadingPhaseViewModel";
import {
  nodeCardWidthFor,
  sideForNode,
} from "./loadingPhaseOrbitLayout";
import {
  STATUS_ACTIVE,
  STATUS_DONE,
  evidenceDetailText,
  evidenceItemKey,
  evidenceMetaText,
  processEventPayload,
  processNodeFullPreviewText,
} from "./loadingPhaseProcessModel";

const iconSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const haloPulse = keyframes`
  0%   { opacity: 0.35; transform: translate(-50%, -50%) scale(0.98); }
  50%  { opacity: 0.65; transform: translate(-50%, -50%) scale(1.02); }
  100% { opacity: 0.35; transform: translate(-50%, -50%) scale(0.98); }
`;

const nodePulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(36, 157, 255, 0.5); }
  70%  { box-shadow: 0 0 0 9px rgba(36, 157, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(36, 157, 255, 0); }
`;

const orbitFadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px) scale(0.985);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const processNodeAppear = keyframes`
  0% { opacity: 0; filter: blur(2px); }
  100% { opacity: 1; filter: blur(0); }
`;

const connectorPulse = keyframes`
  0% { opacity: 0.48; }
  50% { opacity: 1; }
  100% { opacity: 0.48; }
`;

const stageSignalOrbit = keyframes`
  0% {
    opacity: 0.5;
    transform: translate(-50%, -50%) rotate(0deg);
  }
  45% { opacity: 0.82; }
  100% {
    opacity: 0.5;
    transform: translate(-50%, -50%) rotate(360deg);
  }
`;

const connectorPacketToRight = keyframes`
  0% { left: 0%; opacity: 0; transform: translate(-12%, -50%) scaleX(0.65); }
  20% { opacity: 0.95; }
  100% { left: 100%; opacity: 0; transform: translate(-88%, -50%) scaleX(1); }
`;

const connectorPacketToLeft = keyframes`
  0% { left: 100%; opacity: 0; transform: translate(-88%, -50%) scaleX(0.65); }
  20% { opacity: 0.95; }
  100% { left: 0%; opacity: 0; transform: translate(-12%, -50%) scaleX(1); }
`;

const evidencePacketIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(7px) scale(0.985);
    filter: blur(1px);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
`;

const evidenceValueFlash = keyframes`
  0% { text-shadow: 0 0 0 rgba(36, 157, 255, 0); }
  28% { text-shadow: 0 0 16px rgba(36, 157, 255, 0.46); }
  100% { text-shadow: 0 0 0 rgba(36, 157, 255, 0); }
`;

const evidenceIngestSweepFromRight = keyframes`
  0% {
    opacity: 0;
    transform: translateX(30%) scaleX(0.34);
    filter: blur(1px);
  }
  36% { opacity: 0.94; }
  100% {
    opacity: 0;
    transform: translateX(-22%) scaleX(1);
    filter: blur(0);
  }
`;

const evidenceIngestSweepFromLeft = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-30%) scaleX(0.34);
    filter: blur(1px);
  }
  36% { opacity: 0.94; }
  100% {
    opacity: 0;
    transform: translateX(22%) scaleX(1);
    filter: blur(0);
  }
`;

const stackedSpinePulse = keyframes`
  0% { top: 6px; opacity: 0; }
  18% { opacity: 0.92; }
  100% { top: calc(100% - 22px); opacity: 0; }
`;

const stackedHandoffPulse = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-8px) scaleY(0.58);
  }
  28% { opacity: 0.86; }
  100% {
    opacity: 0;
    transform: translateY(122px) scaleY(1);
  }
`;

const layoutMotionTransition = {
  type: "spring",
  stiffness: 420,
  damping: 34,
  mass: 0.78,
};

const evidenceMotionTransition = {
  type: "spring",
  stiffness: 480,
  damping: 36,
  mass: 0.72,
};

const statusColor = (status, activeColor = "#249DFF") => {
  if (status === STATUS_DONE) return "#32D4A4";
  if (status === STATUS_ACTIVE) return activeColor;
  return "rgba(127, 147, 176, 0.72)";
};

const LoadingPhasesOrbit = ({ phaseText, phaseDetailText, processQueue }) => {
  const theme = useTheme();
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isBelowDesktopCanvas = useMediaQuery("(max-width:760px)");
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const canUseMotion = typeof window !== "undefined" &&
    typeof window.addEventListener === "function" &&
    typeof document !== "undefined";
  const canAnimateLayout = canUseMotion && !reduceMotion;
  const MotionBoxComponent = canUseMotion ? motion.div : "div";
  const MotionPresenceComponent = canUseMotion ? AnimatePresence : React.Fragment;
  const layoutMotionProps = canAnimateLayout ? { layout: true } : {};
  const presenceMotionProps = canUseMotion
    ? { initial: false, mode: canAnimateLayout ? "popLayout" : "sync" }
    : {};
  const effectiveWidth = containerWidth || (isBelowDesktopCanvas ? 700 : isMdDown ? 820 : 1000);
  const useStackedNodes = isMobile || effectiveWidth < 740;
  const isCompactDesktop = !useStackedNodes && effectiveWidth < 900;
  const processMessages = useMemo(() => (
    Array.isArray(processQueue)
      ? processQueue.map((evt) => String(processEventPayload(evt)?.message || "").trim()).filter(Boolean)
      : []
  ), [processQueue]);
  const sportsMode = useMemo(() => (
    [String(phaseText || "").trim(), ...processMessages].some(isSportsLoadingPhase)
  ), [phaseText, processMessages]);
  const metaPhaseText = sportsMode ? "Checking sports context" : phaseText;
  const phaseMeta = useMemo(() => getLoadingPhaseMeta(metaPhaseText, isDarkMode), [metaPhaseText]);
  const phaseViewModel = useMemo(() => buildLoadingPhaseViewModel({
    phaseText,
    phaseDetailText,
    phaseMeta,
    processQueue,
    useStackedNodes,
  }), [phaseText, phaseDetailText, phaseMeta, processQueue, useStackedNodes]);
  const {
    centerEvidenceItems,
    evidenceHandoffByKey,
    evidenceHandoffLanes,
    hasEvidenceItems,
    phaseDescriptionText,
    phaseTitleText,
    responseGenerationPhase,
    visibleProcessNodes,
  } = phaseViewModel;

  const {
    canvasHeight,
    canvasWidth,
    centerRadius,
    connectorGap,
    containerMinHeight,
    coreCenterTop,
    coreHeight,
    coreWidth,
    dashedExtra,
    evidencePanelMaxHeight,
    haloExtra,
    phaseDetailMaxHeight,
    processNodePreviewMaxHeight,
    ringExtra,
    stackedNodesTop,
  } = loadingPhaseDimensionsFor({
    hasEvidenceItems,
    isCompactDesktop,
    useStackedNodes,
    visibleProcessNodeCount: visibleProcessNodes.length,
  });
  const orbSize = useStackedNodes ? 48 : isCompactDesktop ? 52 : 60;
  const corePaddingTop = `${orbSize / 2 + (useStackedNodes ? 8 : isCompactDesktop ? 10 : 12)}px`;
  const corePaddingBottom = useStackedNodes ? "9px" : isCompactDesktop ? "10px" : "11px";

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      const nextWidth = node.getBoundingClientRect?.().width || 0;
      setContainerWidth((currentWidth) => (
        Math.abs(currentWidth - nextWidth) > 1 ? nextWidth : currentWidth
      ));
    };

    updateWidth();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(node);
      return () => observer.disconnect();
    }

    if (typeof window !== "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }

    return undefined;
  }, []);

  return (
    <Box
      ref={containerRef}
      data-loading-phase-orbit="true"
      data-loading-phase-response-generation={responseGenerationPhase ? "true" : undefined}
      sx={{
        position: "relative",
        width: "100%",
        minHeight: containerMinHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        py: useStackedNodes ? 1 : 1.5,
        pointerEvents: "none",
      }}
    >
      <Box
        data-loading-phase-canvas="true"
        sx={{
          position: "relative",
          width: canvasWidth,
          height: canvasHeight,
          maxWidth: "100%",
          maxHeight: "100%",
          isolation: "isolate",
          animation: `${orbitFadeIn} 360ms cubic-bezier(0.22, 1, 0.36, 1) both`,
          willChange: "opacity, transform",
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: coreCenterTop,
            zIndex: 0,
            width: coreWidth + haloExtra,
            height: coreHeight + (useStackedNodes ? 68 : isCompactDesktop ? 88 : 122),
            borderRadius: useStackedNodes ? "34px" : isCompactDesktop ? "40px" : "48px",
            transform: "translate(-50%, -50%)",
            background: phaseMeta.haloBackground,
            animation: `${haloPulse} 2.4s ease-in-out infinite`,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: coreCenterTop,
            zIndex: 1,
            width: coreWidth + dashedExtra,
            height: coreHeight + (useStackedNodes ? 38 : isCompactDesktop ? 46 : 64),
            borderRadius: useStackedNodes ? "30px" : isCompactDesktop ? "34px" : "42px",
            border: `1px dashed ${phaseMeta.dashedBorder}`,
            transform: "translate(-50%, -50%)",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: coreCenterTop,
            zIndex: 2,
            width: coreWidth + ringExtra,
            height: coreHeight + (useStackedNodes ? 16 : isCompactDesktop ? 20 : 24),
            borderRadius: useStackedNodes ? "27px" : isCompactDesktop ? "31px" : "38px",
            border: `2px solid ${phaseMeta.ringBorder}`,
            boxShadow: phaseMeta.ringGlow,
            transform: "translate(-50%, -50%)",
          }}
        />

        <Box
          data-loading-phase-stage-ring={phaseMeta.kind}
          sx={{
            position: "absolute",
            left: "50%",
            top: coreCenterTop,
            zIndex: 3,
            width: coreWidth + ringExtra + (useStackedNodes ? 10 : 16),
            height: coreHeight + (useStackedNodes ? 26 : isCompactDesktop ? 32 : 38),
            borderRadius: useStackedNodes ? "29px" : isCompactDesktop ? "34px" : "40px",
            border: `1px solid ${phaseMeta.ringBorder}`,
            background: `radial-gradient(circle at 50% -2px, ${phaseMeta.secondaryAccent} 0 2px, ${phaseMeta.accent} 3px, transparent 10px)`,
            opacity: 0.62,
            transform: "translate(-50%, -50%)",
            transformOrigin: "center",
            filter: isDarkMode ? "drop-shadow(0 0 8px rgba(36,157,255,0.18))" : "drop-shadow(0 0 7px rgba(30,118,230,0.12))",
            animation: `${stageSignalOrbit} ${phaseMeta.kind === "search" ? 5.4 : 6.6}s linear infinite`,
            pointerEvents: "none",
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
            },
          }}
        />

        {!useStackedNodes && visibleProcessNodes.map((node, index) => {
          const status = node.status;
          const color = statusColor(status, phaseMeta.accent);
          const side = sideForNode(node, index);
          const sideNodes = visibleProcessNodes.filter((candidate, idx) => sideForNode(candidate, idx) === side);
          const sideIndex = sideNodes.findIndex((candidate) => candidate.key === node.key);
          const sideCount = Math.max(sideNodes.length, 1);
          const spread = Math.min(coreHeight + 92, 292);
          const labelWidth = nodeCardWidthFor(node, isCompactDesktop);
          const yOffset = sideCount === 1
            ? 0
            : -spread / 2 + (spread / (sideCount - 1)) * sideIndex;
          const lineLeft = side === 1
            ? `calc(50% + ${coreWidth / 2}px)`
            : `calc(50% - ${coreWidth / 2 + connectorGap}px)`;
          const lineWidth = connectorGap;
          const coreDotLeft = `calc(50% + ${side * (coreWidth / 2)}px)`;
          const labelDotLeft = side === 1
            ? `calc(50% + ${coreWidth / 2 + connectorGap}px)`
            : `calc(50% - ${coreWidth / 2 + connectorGap}px)`;
          const labelLeft = side === 1
            ? `calc(50% + ${coreWidth / 2 + connectorGap}px)`
            : `calc(50% - ${coreWidth / 2 + connectorGap + labelWidth}px)`;
          const lineGradient = side === 1
            ? `linear-gradient(90deg, rgba(36, 157, 255, 0.16), ${color})`
            : `linear-gradient(90deg, ${color}, rgba(36, 157, 255, 0.16))`;
          const handoffLane = evidenceHandoffLanes.find((lane) => lane.nodeKey === node.key);
          const handoffAnimation = side === 1 ? connectorPacketToLeft : connectorPacketToRight;
          const nodePreviewText = processNodeFullPreviewText(node);

          return (
            <React.Fragment key={node.key}>
              <Box
                data-loading-phase-connector={node.stageKey || node.key}
                data-loading-phase-status={status}
                sx={{
                  position: "absolute",
                  zIndex: 2,
                  left: lineLeft,
                  top: `calc(50% + ${yOffset}px)`,
                  width: lineWidth,
                  height: "1.5px",
                  background: lineGradient,
                  opacity: status === STATUS_ACTIVE ? 0.95 : 0.58,
                  transform: "translateY(-50%)",
                  overflow: "visible",
                  transition: "left 320ms cubic-bezier(0.22, 1, 0.36, 1), top 320ms cubic-bezier(0.22, 1, 0.36, 1), width 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease",
                  animation: status === STATUS_ACTIVE
                    ? `${connectorPulse} 1.45s ease-in-out infinite`
                    : `${processNodeAppear} 260ms ease-out both`,
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                  },
                }}
              >
                {status === STATUS_ACTIVE && (
                  <Box
                    data-loading-phase-connector-packet={node.stageKey || node.key}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      width: Math.max(16, Math.min(lineWidth * 0.6, 24)),
                      height: 2.5,
                      borderRadius: 999,
                      background: side === 1
                        ? `linear-gradient(90deg, rgba(255,255,255,0), ${phaseMeta.secondaryAccent}, ${color})`
                        : `linear-gradient(90deg, ${color}, ${phaseMeta.secondaryAccent}, rgba(255,255,255,0))`,
                      boxShadow: `0 0 10px ${color}`,
                      animation: `${side === 1 ? connectorPacketToLeft : connectorPacketToRight} 1.35s cubic-bezier(0.16, 1, 0.3, 1) infinite`,
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                        opacity: 0,
                      },
                    }}
                  />
                )}
                {handoffLane && (
                  <Box
                    data-loading-phase-evidence-handoff-packet={`${handoffLane.nodeKey}-${handoffLane.evidenceIndex}`}
                    data-loading-phase-evidence-handoff-side={side === 1 ? "right" : "left"}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      width: Math.max(12, Math.min(lineWidth * 0.5, 20)),
                      height: 3,
                      borderRadius: 999,
                      background: side === 1
                        ? `linear-gradient(90deg, rgba(255,255,255,0), ${phaseMeta.secondaryAccent}, ${phaseMeta.accent})`
                        : `linear-gradient(90deg, ${phaseMeta.accent}, ${phaseMeta.secondaryAccent}, rgba(255,255,255,0))`,
                      boxShadow: `0 0 12px ${phaseMeta.accent}`,
                      animation: `${handoffAnimation} 1.16s cubic-bezier(0.16, 1, 0.3, 1) ${handoffLane.evidenceIndex * 180}ms infinite`,
                      pointerEvents: "none",
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                        opacity: 0,
                      },
                    }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  zIndex: 3,
                  left: coreDotLeft,
                  top: `calc(50% + ${yOffset}px)`,
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: color,
                  border: `1px solid ${isDarkMode ? "#0f1520" : "#ffffff"}`,
                  transform: "translate(-50%, -50%)",
                  opacity: 0.9,
                  transition: "left 320ms cubic-bezier(0.22, 1, 0.36, 1), top 320ms cubic-bezier(0.22, 1, 0.36, 1), background-color 180ms ease",
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  zIndex: 5,
                  left: labelDotLeft,
                  top: `calc(50% + ${yOffset}px)`,
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: color,
                  border: `2px solid ${isDarkMode ? "#0f1520" : "#ffffff"}`,
                  transform: "translate(-50%, -50%)",
                  transition: "left 320ms cubic-bezier(0.22, 1, 0.36, 1), top 320ms cubic-bezier(0.22, 1, 0.36, 1), background-color 180ms ease",
                  animation: status === STATUS_ACTIVE ? `${nodePulse} 1.6s ease-out infinite` : "none",
                }}
              />

              <Box
                data-loading-phase-process-node={node.stageKey || node.key}
                data-loading-phase-status={status}
                sx={{
                  position: "absolute",
                  zIndex: 6,
                  left: labelLeft,
                  top: `calc(50% + ${yOffset}px)`,
                  transform: "translateY(-50%)",
                  width: labelWidth,
                  boxSizing: "border-box",
                  transition: "left 360ms cubic-bezier(0.22, 1, 0.36, 1), top 360ms cubic-bezier(0.22, 1, 0.36, 1), width 360ms cubic-bezier(0.22, 1, 0.36, 1)",
                }}
              >
                <Box
                  component={MotionBoxComponent}
                  {...(canUseMotion ? {
                    ...layoutMotionProps,
                    initial: reduceMotion ? false : { opacity: 0, scale: 0.985 },
                    animate: reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1 },
                    transition: layoutMotionTransition,
                  } : {})}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.65,
                    px: 1,
                    py: 0.9,
                    borderRadius: "8px",
                    border: `1px solid ${phaseMeta.labelBorder}`,
                    backgroundColor: phaseMeta.labelBackground,
                    color: phaseMeta.labelColor,
                    width: "100%",
                    boxSizing: "border-box",
                    animation: `${processNodeAppear} 260ms ease-out both`,
                    "@media (prefers-reduced-motion: reduce)": {
                      animation: "none",
                    },
                  }}
                >
                  {status === STATUS_DONE ? (
                    <CheckRoundedIcon sx={{ fontSize: 13, color, flex: "0 0 auto" }} />
                  ) : (
                    <AutorenewRounded
                      sx={{
                        fontSize: 12,
                        color,
                        animation: `${iconSpin} 1.2s linear infinite`,
                        flex: "0 0 auto",
                      }}
                    />
                  )}
                  <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.2 }}>
                    <Typography
                      sx={{
                        fontSize: "12.75px",
                        fontWeight: 700,
                        lineHeight: 1.24,
                        letterSpacing: 0,
                        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        hyphens: "auto",
                      }}
                    >
                      {node.label}
                    </Typography>
                    {nodePreviewText && (
                      <LoadingPhaseReadableText
                        color={phaseMeta.panelMutedColor}
                        dataRole="node-preview"
                        fontSize="11.75px"
                        fontWeight={600}
                        lineHeight={1.32}
                        maxHeight={processNodePreviewMaxHeight}
                        text={nodePreviewText}
                        phaseMeta={phaseMeta}
                        reduceMotion={reduceMotion}
                        whiteSpace="pre-line"
                        windowSx={{
                          mt: 0.15,
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </React.Fragment>
          );
        })}

        <Box
          data-loading-phase-core="true"
          sx={{
            position: "absolute",
            zIndex: 4,
            left: "50%",
            top: coreCenterTop,
            transform: "translate(-50%, -50%)",
            width: coreWidth,
            minHeight: coreHeight,
            borderRadius: centerRadius,
            border: `1px solid ${phaseMeta.centerBorder}`,
            background: phaseMeta.centerBackground,
            boxShadow: isDarkMode
              ? "0 18px 42px rgba(0,0,0,0.48)"
              : "0 16px 40px rgba(27, 97, 191, 0.22)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            textAlign: "center",
            px: useStackedNodes ? 1.1 : isCompactDesktop ? 1.35 : 1.55,
            pt: corePaddingTop,
            pb: corePaddingBottom,
          }}
        >
          <Box
            data-loading-phase-orb-shell="true"
            sx={{
              position: "absolute",
              zIndex: 5,
              left: "50%",
              top: `-${orbSize / 2}px`,
              transform: "translateX(-50%)",
              width: orbSize,
              height: orbSize,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "transparent",
              color: phaseMeta.accent,
              overflow: "hidden",
              pointerEvents: "none",
            }}
          >
            <Box
              component="img"
              src="/incite_spinning_orb.gif"
              alt=""
              aria-hidden="true"
              data-loading-phase-orb-gif="true"
              sx={{
                width: "100%",
                height: "100%",
                display: "block",
                objectFit: "cover",
              }}
            />
          </Box>
          <Typography
            data-loading-phase-title="true"
            data-loading-phase-title-wrap="word-boundary"
            sx={{
              fontSize: useStackedNodes ? "12.25px" : isCompactDesktop ? "13.5px" : "14.75px",
              lineHeight: 1.22,
              color: phaseMeta.titleColor,
              fontWeight: 800,
              letterSpacing: 0,
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              maxWidth: "100%",
              whiteSpace: "normal",
              overflowWrap: "normal",
              wordBreak: "normal",
              hyphens: "none",
            }}
          >
            {phaseTitleText}
          </Typography>
          {phaseDescriptionText && (
            <LoadingPhaseReadableText
              hasEvidenceItems={hasEvidenceItems}
              fontSize={useStackedNodes ? "9.75px" : isCompactDesktop ? "10.15px" : "10.5px"}
              leftAlignMinLines={3}
              maxHeight={phaseDetailMaxHeight}
              text={phaseDescriptionText}
              phaseMeta={phaseMeta}
              reduceMotion={reduceMotion}
            />
          )}
          {hasEvidenceItems && (
            <Box
              data-loading-phase-evidence-panel="true"
              sx={{
                mt: isMobile ? 1.05 : 1.15,
                width: "100%",
                maxWidth: useStackedNodes ? 204 : isCompactDesktop ? 238 : 280,
                maxHeight: evidencePanelMaxHeight,
                overflowY: "auto",
                overscrollBehavior: "contain",
                pointerEvents: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: `${phaseMeta.panelBorder} transparent`,
                "&::-webkit-scrollbar": {
                  width: 4,
                },
                "&::-webkit-scrollbar-thumb": {
                  borderRadius: 999,
                  backgroundColor: phaseMeta.panelBorder,
                },
                px: useStackedNodes ? 1.05 : isCompactDesktop ? 1.05 : 1.2,
                py: useStackedNodes ? 0.9 : isCompactDesktop ? 0.85 : 1,
                borderRadius: "8px",
                bgcolor: isDarkMode ? "rgba(9,16,28,0.52)" : "rgba(255,255,255,0.68)",
                border: `1px solid ${phaseMeta.panelBorder}`,
                boxShadow: isDarkMode
                  ? "0 8px 18px rgba(0,0,0,0.22)"
                  : "0 8px 16px rgba(18,72,145,0.08)",
                boxSizing: "border-box",
              }}
            >
              <MotionPresenceComponent {...presenceMotionProps}>
                {centerEvidenceItems.map((item, index) => {
                  const itemKey = evidenceItemKey(item);
                  const handoffLane = evidenceHandoffByKey.get(itemKey) || evidenceHandoffByKey.get(`index-${index}`);
                  const handoffSide = handoffLane?.side || 1;
                  const ingestAnimation = handoffSide === 1
                    ? evidenceIngestSweepFromRight
                    : evidenceIngestSweepFromLeft;

                  return (
                    <Box
                      component={MotionBoxComponent}
                      key={itemKey || `${item.label}-${item.value}-${index}`}
                      data-loading-phase-evidence-row={item.stageKey || item.type || "evidence"}
                      data-loading-phase-evidence-type={item.type || "evidence"}
                      data-loading-phase-evidence-handoff-target={handoffLane?.sourceKey || handoffLane?.nodeKey || undefined}
                      data-loading-phase-evidence-handoff-side={handoffLane ? (handoffSide === 1 ? "right" : "left") : undefined}
                      data-loading-phase-evidence-handoff-align={handoffLane ? "top-border" : undefined}
                      {...(canUseMotion ? {
                        ...layoutMotionProps,
                        initial: reduceMotion ? false : { opacity: 0, y: 8, scale: 0.985 },
                        animate: reduceMotion ? { opacity: index === 0 ? 1 : 0.86 } : { opacity: index === 0 ? 1 : 0.86, y: 0, scale: 1 },
                        exit: reduceMotion ? { opacity: 0 } : { opacity: 0, y: -7, scale: 0.985 },
                        transition: evidenceMotionTransition,
                      } : {})}
                      sx={{
                        position: "relative",
                        isolation: "isolate",
                        textAlign: "left",
                        opacity: index === 0 ? 1 : 0.86,
                        pl: 0.85,
                        pb: index === centerEvidenceItems.length - 1 ? 0 : 0.75,
                        mb: index === centerEvidenceItems.length - 1 ? 0 : 0.75,
                        borderBottom: index === centerEvidenceItems.length - 1
                          ? "none"
                          : `1px solid ${isDarkMode ? "rgba(125,170,235,0.16)" : "rgba(30,118,230,0.12)"}`,
                        animation: `${evidencePacketIn} 360ms cubic-bezier(0.22, 1, 0.36, 1)`,
                        animationDelay: `${index * 70}ms`,
                        animationFillMode: "backwards",
                        willChange: "transform, opacity, filter",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 2,
                          bottom: centerEvidenceItems.length === 1 ? 2 : 8,
                          width: 2,
                          zIndex: 0,
                          borderRadius: 999,
                          background: index === 0
                            ? `linear-gradient(180deg, ${phaseMeta.secondaryAccent}, ${phaseMeta.accent})`
                            : phaseMeta.panelBorder,
                          boxShadow: index === 0 ? `0 0 10px ${phaseMeta.accent}` : "none",
                          opacity: index === 0 ? 0.95 : 0.48,
                        },
                        ...(handoffLane ? {
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            top: -1,
                            ...(handoffSide === 1 ? { right: 0 } : { left: 0 }),
                            width: "46%",
                            height: 2,
                            zIndex: 0,
                            borderRadius: 999,
                            background: handoffSide === 1
                              ? `linear-gradient(270deg, rgba(255,255,255,0), ${phaseMeta.secondaryAccent}, ${phaseMeta.accent})`
                              : `linear-gradient(90deg, rgba(255,255,255,0), ${phaseMeta.secondaryAccent}, ${phaseMeta.accent})`,
                            boxShadow: `0 0 12px ${phaseMeta.accent}`,
                            transformOrigin: handoffSide === 1 ? "right center" : "left center",
                            animation: `${ingestAnimation} 1.24s cubic-bezier(0.16, 1, 0.3, 1) ${index * 140}ms infinite`,
                            pointerEvents: "none",
                            "@media (prefers-reduced-motion: reduce)": {
                              animation: "none",
                              opacity: 0,
                            },
                          },
                        } : {}),
                        "@media (prefers-reduced-motion: reduce)": {
                          animation: "none",
                        },
                        "& > *": {
                          position: "relative",
                          zIndex: 1,
                        },
                      }}
                    >
                    <Typography
                      sx={{
                        fontSize: index === 0
                          ? (useStackedNodes ? "10px" : isCompactDesktop ? "10.25px" : "11px")
                          : (useStackedNodes ? "9.5px" : "10px"),
                        lineHeight: 1.2,
                        color: phaseMeta.panelMutedColor,
                        fontWeight: 700,
                        letterSpacing: 0,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {evidenceMetaText(item) || item.type}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: index === 0
                          ? (useStackedNodes ? "12.75px" : isCompactDesktop ? "13px" : "13.75px")
                          : (useStackedNodes ? "11.75px" : "12.25px"),
                        lineHeight: 1.25,
                        color: phaseMeta.panelTextColor,
                        fontWeight: 800,
                        letterSpacing: 0,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        hyphens: "auto",
                      }}
                    >
                      {item.label}
                    </Typography>
                    {(item.value || item.detail) && (
                      <Typography
                        sx={{
                          mt: 0.25,
                          fontSize: index === 0
                            ? (useStackedNodes ? "11.75px" : isCompactDesktop ? "12px" : "12.75px")
                            : (useStackedNodes ? "10.75px" : "11.25px"),
                          lineHeight: 1.32,
                          color: phaseMeta.panelTextColor,
                          fontWeight: 600,
                          letterSpacing: 0,
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          hyphens: "auto",
                          animation: index === 0 ? `${evidenceValueFlash} 1.35s ease-out 120ms both` : "none",
                          "@media (prefers-reduced-motion: reduce)": {
                            animation: "none",
                          },
                        }}
                      >
                        {item.value || item.detail}
                      </Typography>
                    )}
                    {evidenceDetailText(item, false) && (
                      <Typography
                        sx={{
                          mt: 0.25,
                          fontSize: useStackedNodes ? "10.75px" : "11.5px",
                          lineHeight: 1.3,
                          color: phaseMeta.panelMutedColor,
                          fontWeight: 600,
                          letterSpacing: 0,
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {evidenceDetailText(item, false)}
                      </Typography>
                    )}
                    </Box>
                  );
                })}
              </MotionPresenceComponent>
            </Box>
          )}
        </Box>

        {useStackedNodes && visibleProcessNodes.length > 0 && (
          <Box
            data-loading-phase-mobile-spine="true"
            sx={{
              position: "absolute",
              zIndex: 6,
              left: "50%",
              top: stackedNodesTop,
              transform: "translateX(-50%)",
              width: "min(100%, 284px)",
              display: "flex",
              flexDirection: "column",
              gap: 0.55,
              alignItems: "stretch",
              overflow: "visible",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 12,
                top: 5,
                bottom: 5,
                width: 1,
                borderRadius: 999,
                background: `linear-gradient(180deg, rgba(255,255,255,0), ${phaseMeta.ringBorder}, rgba(255,255,255,0))`,
                opacity: 0.7,
              },
              "&::after": {
                content: '""',
                position: "absolute",
                left: 9,
                top: 6,
                width: 7,
                height: 18,
                borderRadius: 999,
                background: `linear-gradient(180deg, ${phaseMeta.secondaryAccent}, ${phaseMeta.accent})`,
                boxShadow: `0 0 10px ${phaseMeta.accent}`,
                animation: `${stackedSpinePulse} 1.9s cubic-bezier(0.16, 1, 0.3, 1) infinite`,
              },
              "@media (prefers-reduced-motion: reduce)": {
                "&::after": {
                  animation: "none",
                  opacity: 0,
                },
              },
            }}
          >
            {evidenceHandoffLanes.length > 0 && (
              <Box
                data-loading-phase-mobile-handoff-packet="true"
                sx={{
                  position: "absolute",
                  left: 7,
                  top: 6,
                  zIndex: 1,
                  width: 11,
                  height: 24,
                  borderRadius: 999,
                  background: `linear-gradient(180deg, ${phaseMeta.secondaryAccent}, ${phaseMeta.accent})`,
                  boxShadow: `0 0 12px ${phaseMeta.accent}`,
                  opacity: 0,
                  transformOrigin: "center top",
                  animation: `${stackedHandoffPulse} 2.35s cubic-bezier(0.16, 1, 0.3, 1) 160ms infinite`,
                  pointerEvents: "none",
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                    opacity: 0,
                  },
                }}
              />
            )}
            <MotionPresenceComponent {...presenceMotionProps}>
              {visibleProcessNodes.map((node) => {
                const status = node.status;
                const color = statusColor(status, phaseMeta.accent);
                const nodePreviewText = processNodeFullPreviewText(node);
                return (
                  <Box
                    component={MotionBoxComponent}
                    key={node.key}
                    data-loading-phase-process-node={node.stageKey || node.key}
                    data-loading-phase-status={status}
                    {...(canUseMotion ? {
                      ...layoutMotionProps,
                      initial: reduceMotion ? false : { opacity: 0, x: -7, scale: 0.985 },
                      animate: reduceMotion ? { opacity: 1 } : { opacity: 1, x: 0, scale: 1 },
                      exit: reduceMotion ? { opacity: 0 } : { opacity: 0, x: 7, scale: 0.985 },
                      transition: layoutMotionTransition,
                    } : {})}
                    sx={{
                      position: "relative",
                      zIndex: 2,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.7,
                      minHeight: 30,
                      px: 1,
                      py: 0.85,
                      borderRadius: "8px",
                      border: `1px solid ${phaseMeta.labelBorder}`,
                      backgroundColor: phaseMeta.labelBackground,
                      color: phaseMeta.labelColor,
                      animation: `${processNodeAppear} 260ms ease-out both`,
                      willChange: "transform, opacity",
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                      },
                    }}
                  >
                    {status === STATUS_DONE ? (
                      <CheckRoundedIcon sx={{ fontSize: 12, color, flex: "0 0 auto" }} />
                    ) : (
                      <AutorenewRounded
                        sx={{
                          fontSize: 12,
                          color,
                          animation: `${iconSpin} 1.2s linear infinite`,
                          flex: "0 0 auto",
                        }}
                      />
                    )}
                    <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.2 }}>
                      <Typography
                        sx={{
                          fontSize: "11.75px",
                          fontWeight: 700,
                          lineHeight: 1.28,
                          letterSpacing: 0,
                          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          hyphens: "auto",
                        }}
                      >
                        {node.label}
                      </Typography>
                      {nodePreviewText && (
                        <LoadingPhaseReadableText
                          color={phaseMeta.panelMutedColor}
                          dataRole="node-preview"
                          fontSize="11px"
                          fontWeight={600}
                          lineHeight={1.32}
                          maxHeight={processNodePreviewMaxHeight}
                          text={nodePreviewText}
                          phaseMeta={phaseMeta}
                          reduceMotion={reduceMotion}
                          whiteSpace="pre-line"
                          windowSx={{
                            mt: 0.1,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </MotionPresenceComponent>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LoadingPhasesOrbit;
